import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Leaf } from 'lucide-react'
import { classifyChurnReasons, formatBroccoliScore } from '@/lib/public-tools/broccoli'
import type { ReasonClassification, BroccoliResult } from '@/lib/public-tools/broccoli'
import { cn } from '@/lib/utils'

const PLACEHOLDER = `Too expensive: 35%
Missing features: 28%
Switched to an alternative: 20%
No longer needed: 12%
Poor support: 5%`

const CLASSIFICATION_CONFIG = {
  high_social_desirability: { label: 'High broccoli', color: 'text-red-500', bg: 'bg-red-100', barColor: 'bg-red-400' },
  medium_social_desirability: { label: 'Medium broccoli', color: 'text-uncertainty-amber', bg: 'bg-amber-100', barColor: 'bg-uncertainty-amber' },
  low_social_desirability: { label: 'Honest', color: 'text-validation-green', bg: 'bg-green-100', barColor: 'bg-validation-green' },
}

function ScoreDisplay({ result }: { result: BroccoliResult }) {
  const pct = Math.round(result.overall_score * 100)
  const color = pct >= 70 ? 'text-red-500' : pct >= 45 ? 'text-uncertainty-amber' : 'text-validation-green'

  return (
    <div className="space-y-8">
      {/* Big score */}
      <div className="text-center py-8 bg-soft-panel rounded-2xl">
        <p className="text-sm text-slate-500 mb-2">Your Broccoli Score</p>
        <p className={cn('text-7xl font-bold', color)}>{pct}</p>
        <p className="text-slate-400 text-sm mt-1">out of 100</p>
        <p className="text-base text-slate-700 mt-3 font-medium px-4">{formatBroccoliScore(result.overall_score)}</p>
      </div>

      {/* Narrative */}
      <div className="bg-echo-indigo/5 border border-echo-indigo/20 rounded-xl p-4">
        <p className="text-xs font-medium text-echo-indigo mb-1">Analysis</p>
        <p className="text-sm text-slate-700">{result.narrative}</p>
      </div>

      {/* Per-reason table */}
      {result.per_reason.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-deep-ink mb-3">Reason-by-reason breakdown</h3>
          <div className="space-y-3">
            {result.per_reason.map((r, i) => {
              const config = CLASSIFICATION_CONFIG[r.classification]
              const barPct = Math.round(r.broccoli_score * 100)
              return (
                <div key={i} className="border border-border-gray rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-deep-ink">{r.reason}</p>
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium shrink-0', config.bg, config.color)}>
                      {config.label}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full mb-2">
                    <div className={cn('h-full rounded-full', config.barColor)} style={{ width: `${barPct}%` }} />
                  </div>
                  <p className="text-xs text-slate-600 mb-1">{r.rationale}</p>
                  <p className="text-xs text-human-teal font-medium">→ {r.hidden_driver_hint}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export function BroccoliScorePage() {
  const [input, setInput] = useState('')
  const [result, setResult] = useState<BroccoliResult | null>(null)
  const [email, setEmail] = useState('')
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [running, setRunning] = useState(false)

  function handleScore() {
    if (!input.trim()) return
    setRunning(true)
    setTimeout(() => {
      setResult(classifyChurnReasons(input))
      setRunning(false)
    }, 800)
  }

  return (
    <div className="min-h-screen bg-off-white">
      {/* Header */}
      <header className="border-b border-border-gray bg-paper px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-base font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>echosome</Link>
        <Link to="/signup?utm_source=broccoli_tool" className="text-sm bg-echo-indigo text-white px-4 py-2 rounded-lg hover:bg-echo-indigo/90 transition-colors">
          Try free →
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="mb-4 flex justify-center"><Leaf className="w-10 h-10 text-echo-mark" strokeWidth={1.5} /></div>
          <h1 className="text-3xl font-bold text-deep-ink mb-3" style={{ letterSpacing: '-0.03em' }}>
            Your churn survey is probably lying.<br />Find out how much.
          </h1>
          <p className="text-base text-slate-600 max-w-lg mx-auto">
            The Broccoli Score measures social desirability bias in your churn data.
            High broccoli = safe answers that don't tell you why customers really left.
          </p>
        </div>

        {!result ? (
          <div className="space-y-4">
            <textarea
              className="w-full border border-border-gray rounded-xl px-4 py-3 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none bg-paper"
              rows={8}
              placeholder={PLACEHOLDER}
              value={input}
              onChange={e => setInput(e.target.value)}
            />
            <button
              className="w-full bg-echo-indigo text-white py-3 rounded-xl text-sm font-medium hover:bg-echo-indigo/90 transition-colors disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:cursor-not-allowed"
              onClick={handleScore}
              disabled={!input.trim() || running}
            >
              {running ? 'Analyzing…' : 'Get my Broccoli Score →'}
            </button>
            <p className="text-xs text-center text-slate-400">No signup required. Results are instant.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <ScoreDisplay result={result} />

            {/* Email capture */}
            {!emailSubmitted && (
              <div className="border border-border-gray rounded-xl p-5 bg-paper">
                <p className="text-sm font-medium text-deep-ink mb-1">Email me this report</p>
                <p className="text-xs text-slate-500 mb-3">Get a deeper analysis + benchmarks from real churn data. No spam, unsubscribe anytime.</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    className="flex-1 border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
                    placeholder="you@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                  <button
                    className="bg-echo-indigo text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-echo-indigo/90 transition-colors disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:cursor-not-allowed"
                    onClick={() => { if (email) setEmailSubmitted(true) }}
                    disabled={!email.includes('@')}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
            {emailSubmitted && (
              <div className="bg-green-50 border border-validation-green/30 rounded-xl p-4 text-center">
                <p className="text-sm text-validation-green font-medium">✓ Report sent to {email}</p>
              </div>
            )}

            {/* CTA */}
            <div className="bg-echo-indigo rounded-2xl p-6 text-center text-white">
              <p className="text-base font-medium mb-2">Want to validate which reasons are actually real?</p>
              <p className="text-sm text-indigo-200 mb-4">Echosome runs ensemble behavioral analysis against your real customer data — no surveys needed.</p>
              <Link
                to="/signup?utm_source=broccoli_tool&utm_campaign=result_cta"
                className="inline-block bg-white text-echo-indigo px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
              >
                Try Echosome free →
              </Link>
            </div>

            <button
              className="w-full text-sm text-slate-500 hover:text-deep-ink border border-border-gray rounded-xl py-2.5"
              onClick={() => { setResult(null); setInput('') }}
            >
              Score another survey
            </button>
          </div>
        )}

        {/* Trust signals */}
        <div className="mt-12 pt-8 border-t border-border-gray text-center">
          <p className="text-xs text-slate-400">
            Scoring logic based on Nederhof (1985), Paulhus (1991), and Fisher (1993) social desirability bias research.
            No survey data is stored without consent.
          </p>
        </div>
      </main>
    </div>
  )
}
